import { LoanPaymentMethodFields } from "@/components/hr/LoanPaymentMethodFields";
import type { LoanPaymentDraft } from "@/components/hr/LoanPaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import {
  formatMoneyDecimalDisplay,
  formatMoneyInputTyping,
  parseMoneyDecimalInput,
  pkrAmountShortLabel,
} from "@/lib/formatPkrAmount";
import { loanDraftToPaymentDraft } from "@/components/sales/PaymentMethodFields";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { paymentMethodLabel, type SalePaymentLine } from "@/lib/sales/salePaymentLines";
import { cn } from "@/lib/utils";
import { Calculator, Info, Wallet } from "lucide-react";
import { useEffect, useState } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: boolean;
  line: SalePaymentLine;
  onLineChange: (line: SalePaymentLine) => void;
  expectedTotal: number;
  salePrice: number;
  otherLinesTotal: number;
  disabled?: boolean;
  onSave: (andAddMore: boolean) => void | Promise<void>;
};

function maxLineAmount(expectedTotal: number, salePrice: number, otherLinesTotal: number): number {
  const cap = salePrice > 0 ? salePrice : expectedTotal;
  return Math.max(0, Math.round((cap - otherLinesTotal) * 100) / 100);
}

function lineToLoanDraft(line: SalePaymentLine): LoanPaymentDraft {
  const method = line.paymentMethod;
  let referenceNo = "";
  if (method === "online") referenceNo = line.transactionReferenceNo;
  else if (method === "pay_order") referenceNo = line.payOrderNo;
  else if (method === "cash_deposit") referenceNo = line.depositSlipNo ?? line.transactionReferenceNo;

  return {
    paymentMethod: method,
    bankId: line.bankId,
    referenceNo,
    chequeNo: line.chequeNo ?? "",
    chequeDate: line.chequeDate ?? "",
  };
}

export function SalePaymentLineDialog({
  open,
  onOpenChange,
  editing,
  line,
  onLineChange,
  expectedTotal,
  salePrice,
  otherLinesTotal,
  disabled = false,
  onSave,
}: Props) {
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [amountFocused, setAmountFocused] = useState(false);
  const [amountText, setAmountText] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchBanks()
      .then((rows) => {
        if (!cancelled) setBanks(rows);
      })
      .catch(() => {
        if (!cancelled) setBanks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const maxAmount = maxLineAmount(expectedTotal, salePrice, otherLinesTotal);
  const projectedTotal = Math.round((otherLinesTotal + line.amount) * 100) / 100;
  const allocationPercent =
    expectedTotal > 0 ? Math.min(100, Math.round((projectedTotal / expectedTotal) * 100)) : 0;
  const enteredAmountLabel = line.amount > 0 ? pkrAmountShortLabel(line.amount) : "";

  const applyAmount = (next: number) => {
    const capped = Math.min(Math.max(0, next), maxAmount);
    onLineChange({ ...line, amount: Math.round(capped * 100) / 100 });
  };

  const applyPresetPercent = (percent: number) => {
    if (maxAmount <= 0) return;
    applyAmount(Math.round(maxAmount * (percent / 100) * 100) / 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">
                {editing ? "Edit Payment Line" : "Add Payment Line"}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                Split the received amount across cash, online, cheque, or other payment methods.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs mt-4">
          <div className="min-w-0">
            <p className="font-bold text-foreground">Payment allocation</p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold">
              {expectedTotal > 0 ? "Must match amount received" : "Enter payment methods"}
            </p>
          </div>
          <div className="text-right shrink-0 pl-3">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground">Target amount</p>
            <p className="font-bold text-primary text-sm">
              {expectedTotal > 0 ? formatCurrency(expectedTotal) : "—"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 py-2">
          <div className="space-y-4 min-w-0">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground flex justify-between items-center">
                <span>
                  Line amount ({getActiveCompanyCurrency().code}) <span className="text-destructive">*</span>
                </span>
                {enteredAmountLabel ? (
                  <span className="text-xs text-primary font-bold tabular-nums">{enteredAmountLabel}</span>
                ) : null}
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={
                    amountFocused
                      ? amountText
                      : line.amount > 0
                        ? formatMoneyDecimalDisplay(line.amount)
                        : ""
                  }
                  disabled={disabled}
                  onFocus={() => {
                    setAmountFocused(true);
                    setAmountText(line.amount > 0 ? formatMoneyDecimalDisplay(line.amount) : "");
                  }}
                  onBlur={() => {
                    setAmountFocused(false);
                    applyAmount(parseMoneyDecimalInput(amountText));
                  }}
                  onChange={(e) => {
                    const typed = formatMoneyInputTyping(e.target.value);
                    setAmountText(typed);
                    const parsed = parseMoneyDecimalInput(typed);
                    if (parsed > maxAmount + 0.009) {
                      applyAmount(maxAmount);
                      setAmountText(formatMoneyDecimalDisplay(maxAmount));
                      return;
                    }
                    onLineChange({ ...line, amount: parsed });
                  }}
                  placeholder="0.00"
                  inputMode="decimal"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  {getActiveCompanyCurrency().code}
                </span>
              </div>
              {maxAmount > 0 ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="text-[10px] text-muted-foreground self-center mr-1 font-medium">Quick:</span>
                  {[25, 50, 100].map((percent) => (
                    <button
                      key={percent}
                      type="button"
                      onClick={() => applyPresetPercent(percent)}
                      disabled={disabled}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors disabled:opacity-50"
                    >
                      {percent === 100 ? "Max" : `${percent}%`}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <LoanPaymentMethodFields
              value={lineToLoanDraft(line)}
              banks={banks}
              stacked
              onChange={(patch) => {
                const merged = { ...lineToLoanDraft(line), ...patch };
                const paymentDraft = loanDraftToPaymentDraft(merged);
                onLineChange({ ...line, ...paymentDraft });
              }}
            />
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <Calculator className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Live Allocation Preview</h4>
              </div>

              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Other lines</span>
                  <span className="font-bold text-foreground">{formatCurrency(otherLinesTotal)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">This line</span>
                  <span className={cn("font-bold", line.amount > 0 ? "text-emerald-600" : "text-foreground")}>
                    {line.amount > 0 ? `+${formatCurrency(line.amount)}` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Projected total</span>
                  <span className="font-extrabold text-primary text-sm">{formatCurrency(projectedTotal)}</span>
                </div>
                {expectedTotal > 0 ? (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Target</span>
                    <span className="font-bold text-foreground">{formatCurrency(expectedTotal)}</span>
                  </div>
                ) : null}

                {line.paymentMethod ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-1.5 mt-2">
                    <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">
                      Method preview
                    </p>
                    <p className="font-bold text-foreground">{paymentMethodLabel(line.paymentMethod)}</p>
                  </div>
                ) : null}

                {expectedTotal > 0 && line.amount > 0 ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Allocation progress</span>
                      <span className="text-primary">{allocationPercent}%</span>
                    </div>
                    <div className="h-2 w-full bg-primary/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${allocationPercent}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      {Math.abs(projectedTotal - expectedTotal) < 0.01
                        ? "This line completes the required allocation."
                        : projectedTotal > expectedTotal
                          ? "Total exceeds the target amount."
                          : `${formatCurrency(Math.max(0, expectedTotal - projectedTotal))} still unallocated.`}
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    Enter amount and payment method to preview allocation.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Each line posts as a separate cash/bank receipt in the ledger.</span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 border-t border-border/40 pt-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" className="h-10" onClick={() => onOpenChange(false)} disabled={disabled}>
            Cancel
          </Button>
          {!editing ? (
            <Button
              type="button"
              variant="outline"
              className="h-10 px-4 text-xs font-semibold border-primary/40 text-primary hover:bg-primary/10"
              onClick={() => void onSave(true)}
              disabled={disabled}
            >
              Save and Add More
            </Button>
          ) : null}
          <Button
            type="button"
            variant="default"
            className="h-10 px-5 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={() => void onSave(false)}
            disabled={disabled}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
